import React from 'react';
import DemoStage from '../DemoStage.jsx';

/**
 * Animated overview of the New Submission wizard: fill the Step 1 header,
 * continue, pick the event type on Step 2, submit. Mirrors the StepPill
 * chrome from NewCaseWizard.jsx; unfilled fields render as empty inputs with
 * a placeholder bar. Placeholder data only.
 */

const STEPS = [
  {
    caption: 'Step 1 — general info: clinic, date of incident, staff member, and event type.',
    scene: 'step1', ms: 2400,
    cursor: 'f-clinic',
  },
  {
    caption: 'Continue moves you on once the required fields are filled.',
    scene: 'step1-done', ms: 2000,
    cursor: 'next', click: true,
  },
  {
    caption: 'Step 2 asks for event-type details — pick Treatment, Near Miss or Clinic Incident.',
    scene: 'step2', ms: 2400,
    cursor: 'f-event',
  },
  {
    caption: 'The form adapts to your choice; required uploads are listed before you submit.',
    scene: 'step2-filled', ms: 2400,
    cursor: 'next', click: true,
  },
  {
    caption: 'Submitting creates the case with an AE reference and notifies the right people automatically.',
    scene: 'submitted', ms: 3000,
  },
];

function StepPill({ n, label, active, done }) {
  return (
    <div style={{
      display: 'flex', alignItems: 'center', gap: 8, flex: 1, padding: '7px 10px',
      background: active ? 'var(--surface)' : 'var(--surface-2)',
      border: '1px solid', borderColor: active ? 'var(--brand)' : 'var(--border)',
      borderRadius: 10, transition: 'all 300ms var(--ease)',
    }}>
      <span style={{
        display: 'grid', placeItems: 'center', width: 20, height: 20, borderRadius: '50%',
        background: active ? 'var(--brand)' : (done ? 'var(--ok)' : 'var(--surface-3)'),
        color: (active || done) ? '#fff' : 'var(--text-3)',
        fontWeight: 600, fontSize: 11, flexShrink: 0, transition: 'background 300ms var(--ease)',
      }}>
        {done ? '✓' : n}
      </span>
      <span style={{ fontSize: 12, fontWeight: 600, color: active ? 'var(--text)' : 'var(--text-2)' }}>
        Step {n} · {label}
      </span>
    </div>
  );
}

function Field({ label, filled, value, target }) {
  return (
    <div data-kb-target={target}>
      <div className="kb-mock-label">{label}</div>
      <div className="kb-mock-input">
        {filled ? value : <span className="kb-skel" style={{ width: '55%' }} />}
      </div>
    </div>
  );
}

export default function WizardStepsDemo() {
  return (
    <DemoStage steps={STEPS} label="The two-step submission wizard" height={230}>
      {(step) => {
        const scene = step?.scene ?? 'step1';
        const onStep2 = scene === 'step2' || scene === 'step2-filled';
        const submitted = scene === 'submitted';
        const filled = scene === 'step1-done' || scene === 'step2-filled';
        return (
          <div className="kb-mock">
            <div style={{ display: 'flex', gap: 8, marginBottom: 14 }}>
              <StepPill n={1} label="General info" active={!onStep2 && !submitted} done={onStep2 || submitted} />
              <StepPill n={2} label="Event details" active={onStep2} done={submitted} />
            </div>

            {submitted ? (
              <div className="kb-mock-success kb-pop">
                <span className="kb-mock-tick">✓</span>
                <div>
                  <div style={{ fontWeight: 600, fontSize: 13.5 }}>Case submitted</div>
                  <div style={{ fontSize: 12.5, color: 'var(--text-2)', marginTop: 2 }}>
                    Reference <span className="badge event-type">AE-042</span> · Clinic_A
                  </div>
                </div>
              </div>
            ) : (
              <>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px 14px' }}>
                  {onStep2 ? (
                    <>
                      <Field label="Event type" filled={filled} value="Treatment" target="f-event" />
                      <Field label="Treatment type" filled={filled} value="Laser hair removal" />
                      <Field label="Severity code" filled={filled} value="Code 2" />
                      <Field label="Incident category" filled={filled} value="Blistering" />
                    </>
                  ) : (
                    <>
                      <Field label="Clinic" filled={filled} value="Clinic_A" target="f-clinic" />
                      <Field label="Date of incident" filled={filled} value="28 Jun 2026" />
                      <Field label="Staff member" filled={filled} value="J. Doyle" />
                      <Field label="Clinic Manager" filled value="auto-filled" />
                    </>
                  )}
                </div>
                <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 12 }}>
                  <button type="button" className="btn primary sm" tabIndex={-1} data-kb-target="next">
                    {onStep2 ? 'Submit case' : 'Continue'}
                  </button>
                </div>
              </>
            )}
          </div>
        );
      }}
    </DemoStage>
  );
}
